import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Star, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", skills: [], equipment: [], day_rate: 0, rating: 0, notes: "" };

export default function Freelancers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/freelancers").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      const payload = {
        ...form,
        skills: typeof form.skills === "string" ? form.skills.split(",").map(s => s.trim()).filter(Boolean) : form.skills,
        equipment: typeof form.equipment === "string" ? form.equipment.split(",").map(s => s.trim()).filter(Boolean) : form.equipment,
        day_rate: Number(form.day_rate) || 0,
        rating: Number(form.rating) || 0,
      };
      if (editId) await api.put(`/freelancers/${editId}`, payload);
      else await api.post("/freelancers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const edit = (f) => { setForm({ ...f, skills: f.skills.join(", "), equipment: f.equipment.join(", ") }); setEditId(f.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/freelancers/${id}`); load(); };

  const inp = "w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:border-white/30 focus:outline-none";
  const lbl = "text-[10px] uppercase tracking-widest text-white/40";

  return (
    <>
      <PageHeader title="Freelancers" subtitle={`${list.length} in database`}
        actions={<button data-testid="new-freelancer-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="bg-white text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 hover:bg-white/90 active:scale-[0.98]"><Plus size={14} weight="bold" /> New</button>}
      />
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {list.map(f => (
          <div key={f.id} data-testid={`freelancer-card-${f.id}`} className="hairline rounded-lg p-4 bg-[#0F0F0F] hover:bg-[#131313] transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold shrink-0">{f.name[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{f.name}</div>
                <div className="text-[11px] text-white/50 truncate">{f.email || f.phone}</div>
              </div>
              <div className="flex items-center gap-0.5 text-amber-400 text-xs">
                <Star size={12} weight="fill" /> <span className="mono">{f.rating?.toFixed(1) || "—"}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {f.skills?.slice(0, 4).map(s => <span key={s} className="text-[10px] px-2 py-0.5 rounded border border-white/10 bg-white/[0.03] text-white/70">{s}</span>)}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-white/5 text-center">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-white/40">Day rate</div>
                <div className="mono text-xs font-bold mt-0.5">{fmtINR(f.day_rate)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-white/40">Earned</div>
                <div className="mono text-xs font-bold text-emerald-400 mt-0.5">{fmtINR(f.total_earned)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-white/40">Due</div>
                <div className="mono text-xs font-bold text-amber-400 mt-0.5">{fmtINR(f.outstanding)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-3">
              <button data-testid={`edit-freelancer-${f.id}`} onClick={() => edit(f)} className="text-white/40 hover:text-white p-1"><PencilSimple size={12} /></button>
              <button onClick={() => del(f.id)} className="text-white/40 hover:text-red-400 p-1"><Trash size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Freelancer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className={lbl}>Name*</label><input data-testid="freelancer-input-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inp} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Email</label><input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inp} /></div>
              <div><label className={lbl}>Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inp} /></div>
            </div>
            <div><label className={lbl}>Skills (comma separated)</label><input value={form.skills} onChange={e => setForm({...form, skills: e.target.value})} className={inp} /></div>
            <div><label className={lbl}>Equipment (comma separated)</label><input value={form.equipment} onChange={e => setForm({...form, equipment: e.target.value})} className={inp} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Day Rate</label><input type="number" value={form.day_rate} onChange={e => setForm({...form, day_rate: e.target.value})} className={inp + " mono"} /></div>
              <div><label className={lbl}>Rating</label><input type="number" step="0.1" value={form.rating} onChange={e => setForm({...form, rating: e.target.value})} className={inp + " mono"} /></div>
            </div>
            <div><label className={lbl}>Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className={inp} rows="2" /></div>
          </div>
          <DialogFooter><button data-testid="save-freelancer-btn" onClick={save} className="bg-white text-black text-xs font-semibold px-4 py-2 rounded-md hover:bg-white/90">Save</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
